import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Staff {
  id: number;
  agency_id: number;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  staff_type: string | null;
  is_active: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StaffList() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [staffType, setStaffType] = useState("");
  const [showInactive, setShowInactive] = useState(true);

  const { data: staffList, isLoading } = useQuery<Staff[]>({
    queryKey: ["admin-staff", showInactive],
    queryFn: async () => {
      const res = await api.get<{ data: Staff[] }>(
        "/admin/programs/staff/list",
        { params: { includeInactive: String(showInactive) } },
      );
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      firstName?: string;
      lastName?: string;
      staffType?: string;
    }) => api.post("/admin/programs/staff", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-staff"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      firstName?: string;
      lastName?: string;
      staffType?: string;
      isActive?: boolean;
    }) => api.put(`/admin/programs/staff/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-staff"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/programs/staff/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-staff"] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setEditing(null);
    setFirstName("");
    setLastName("");
    setStaffType("");
  }

  function handleEdit(staff: Staff) {
    setEditing(staff);
    setFirstName(staff.first_name ?? "");
    setLastName(staff.last_name ?? "");
    setStaffType(staff.staff_type ?? "");
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      staffType: staffType || undefined,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function toggleActive(staff: Staff) {
    updateMutation.mutate({
      id: staff.id,
      isActive: !staff.is_active,
    });
  }

  const activeCount = (staffList ?? []).filter((s) => s.is_active).length;
  const inactiveCount = (staffList ?? []).length - activeCount;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Staff</h1>
          <p className="text-sm text-muted-foreground">
            {activeCount} active, {inactiveCount} inactive staff members
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4" />
            New Staff
          </Button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editing ? "Edit Staff Member" : "New Staff Member"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                />
                <Input
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Smith"
                />
                <Input
                  label="Type / Role"
                  value={staffType}
                  onChange={(e) => setStaffType(e.target.value)}
                  placeholder="e.g. Advocate, Counselor"
                />
              </div>

              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {((createMutation.error ?? updateMutation.error) as Error)
                    ?.message ?? "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  loading={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editing ? "Save Changes" : "Add Staff"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {/* Empty */}
      {!isLoading && (staffList ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No staff members configured yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Staff table */}
      {!isLoading && (staffList ?? []).length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(staffList ?? []).map((staff) => {
                  const name =
                    staff.full_name ??
                    ([staff.first_name, staff.last_name]
                      .filter(Boolean)
                      .join(" ") ||
                    "(Unnamed)");

                  return (
                    <tr
                      key={staff.id}
                      className={cn(
                        "border-b last:border-0 hover:bg-muted/30",
                        !staff.is_active && "opacity-60",
                      )}
                    >
                      <td className="px-4 py-3 font-medium">{name}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {staff.staff_type ?? "-"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(staff)}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                            staff.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600",
                          )}
                        >
                          {staff.is_active ? (
                            <>
                              <CheckCircle className="h-3 w-3" /> Active
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3" /> Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(staff)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (
                                confirm(
                                  "Delete this staff member? This cannot be undone.",
                                )
                              ) {
                                deleteMutation.mutate(staff.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
